import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { debounce } from "lodash";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { PMDialog } from "../../utils/PMDialog";
import { CustomerSelector } from "../../utils/moduleSelectors/customerSelector";
import { ProjectSelector } from "../../utils/moduleSelectors/projectSelector";
import { fetchOffers, saveOfferAs, overwriteOffer, saveOfferAsTemplate, saveOfferToTemplate } from "../../../api/offer";
import { resolveOfferActionErrorMessage } from "./utils";

/**
 * Debounced offer search — shared by the overwrite target picker and the
 * «Oppdater tilbudsmal» template picker. `templatesOnly` client-filters the
 * result to `is_template` rows since the frozen `/offers/list` contract
 * doesn't specify a dedicated template filter param.
 */
function useOfferSearch({ open, excludeOfferId, templatesOnly = false }) {
  const [query, setQuery] = useState("");
  const [offers, setOffers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const searchRef = useRef(
    debounce(async (q) => {
      setIsLoading(true);
      try {
        const params = { query: q || "", per_page: 20, page: 1 };
        if (templatesOnly) params.is_template = true;
        const response = await fetchOffers(params);
        let rows = Array.isArray(response?.data) ? response.data : [];
        if (templatesOnly) rows = rows.filter((o) => o?.is_template);
        if (excludeOfferId) rows = rows.filter((o) => o.id !== excludeOfferId);
        setOffers(rows);
      } catch (err) {
        setOffers([]);
      } finally {
        setIsLoading(false);
      }
    }, 300)
  );

  useEffect(() => {
    if (!open) return;
    setQuery("");
    searchRef.current("");
    return () => searchRef.current.cancel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onQueryChange = useCallback((value) => {
    setQuery(value);
    searchRef.current(value);
  }, []);

  return { query, onQueryChange, offers, isLoading };
}

function OfferPickerList({ offers, isLoading, selectedId, onSelect, emptyLabel }) {
  if (isLoading && offers.length === 0) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-slate-200 border-t-[#1463C7]" />
      </div>
    );
  }
  if (offers.length === 0) {
    return <p className="py-6 text-center text-sm text-slate-500">{emptyLabel}</p>;
  }
  return (
    <ul className="max-h-64 divide-y divide-slate-100 overflow-y-auto rounded-lg border border-slate-200">
      {offers.map((o) => {
        const selected = selectedId === o.id;
        return (
          <li key={o.id}>
            <button
              type="button"
              onClick={() => onSelect(o)}
              className={`flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left text-sm transition-colors ${
                selected ? "bg-blue-50" : "hover:bg-slate-50"
              }`}
            >
              <span className="font-medium text-slate-900">
                {o.title || o.displayTitle || `#${o.id}`}
              </span>
              <span className="text-xs text-slate-500">
                {[o.offer_number, o.customer?.name || o.customer?.displayTitle]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function SearchInput({ value, onChange, placeholder }) {
  return (
    <div className="relative">
      <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-lg border border-slate-300 py-2 pl-9 pr-3 text-sm focus:border-[#1463C7] focus:outline-none focus:ring-1 focus:ring-[#1463C7]"
      />
    </div>
  );
}

/** «Lagre som nytt tilbud» — POST /offers/:id/save_as → navigate to the new offer. */
export function OfferSaveAsDialog({ open, onClose, offer }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [reference, setReference] = useState("");
  const [customerId, setCustomerId] = useState(null);
  const [projectId, setProjectId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setReference("");
      setCustomerId(null);
      setProjectId(null);
    }
  }, [open]);

  const handleClose = () => {
    if (submitting) return;
    onClose?.();
  };

  const handleConfirm = async () => {
    if (!offer?.id) return;
    setSubmitting(true);
    try {
      const payload = {};
      if (reference.trim()) payload.reference = reference.trim();
      if (customerId) payload.customer_id = customerId;
      if (projectId) payload.project_id = projectId;

      const result = await saveOfferAs(offer.id, payload);
      if (result?.error) {
        toast.error(
          resolveOfferActionErrorMessage(t, result, "offers.save_as.failed")
        );
        return;
      }
      const newOfferId = result?.offer?.id || result?.id;
      toast.success(t("offers.save_as.success"));
      onClose?.();
      if (newOfferId) navigate(`/dashboard/offers/${newOfferId}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PMDialog
      open={open}
      onClose={handleClose}
      title="offers.save_as.title"
      size="md"
      closeOnOutsideClick={!submitting}
    >
      <div className="space-y-4">
        <p className="text-sm text-slate-600">{t("offers.save_as.description")}</p>
        <div>
          <label className="mb-1 block text-xs font-semibold text-slate-600">
            {t("offers.save_as.reference_label")}
          </label>
          <input
            type="text"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-[#1463C7] focus:outline-none focus:ring-1 focus:ring-[#1463C7]"
            placeholder={t("offers.save_as.reference_placeholder")}
          />
        </div>
        <CustomerSelector
          key={open ? "save-as-open" : "save-as-closed"}
          name="customer_id"
          label={t("offers.save_as.customer_label")}
          selected={null}
          setFieldValue={(_name, value) => setCustomerId(value || null)}
        />
        <ProjectSelector
          key={open ? "save-as-project-open" : "save-as-project-closed"}
          name="project_id"
          label={t("offers.save_as.project_label")}
          selected={null}
          setFieldValue={(_name, value) => setProjectId(value || null)}
        />
        <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={handleClose}
            disabled={submitting}
            className="inline-flex justify-center rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-50"
          >
            {t("Cancel")}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={submitting}
            className="inline-flex justify-center rounded-lg border border-transparent bg-[#1463C7] px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-[#0B3A78] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting ? t("Loading...") : t("offers.save_as.confirm")}
          </button>
        </div>
      </div>
    </PMDialog>
  );
}

/** «Overskriv eksisterende tilbud» — strong confirm + POST /offers/:id/overwrite. */
export function OfferOverwriteDialog({ open, onClose, offer, onOverwritten }) {
  const { t } = useTranslation();
  const [target, setTarget] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { query, onQueryChange, offers, isLoading } = useOfferSearch({
    open,
    excludeOfferId: offer?.id,
  });

  useEffect(() => {
    if (open) {
      setTarget(null);
      setConfirmText("");
    }
  }, [open]);

  const expectedConfirmText = target?.offer_number || String(target?.id || "");
  const canConfirm = target && confirmText.trim() === expectedConfirmText;

  const handleClose = () => {
    if (submitting) return;
    onClose?.();
  };

  const handleConfirm = async () => {
    if (!offer?.id || !target?.id || !canConfirm) return;
    setSubmitting(true);
    try {
      const result = await overwriteOffer(offer.id, target.id);
      if (result?.error) {
        toast.error(
          resolveOfferActionErrorMessage(t, result, "offers.overwrite.failed")
        );
        return;
      }
      toast.success(t("offers.overwrite.success", { number: expectedConfirmText }));
      onClose?.();
      await onOverwritten?.(result);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PMDialog
      open={open}
      onClose={handleClose}
      title="offers.overwrite.title"
      size="md"
      closeOnOutsideClick={!submitting}
    >
      <div className="space-y-4">
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          {t("offers.overwrite.warning")}
        </div>
        <SearchInput
          value={query}
          onChange={onQueryChange}
          placeholder={t("offers.overwrite.search_placeholder")}
        />
        <OfferPickerList
          offers={offers}
          isLoading={isLoading}
          selectedId={target?.id}
          onSelect={setTarget}
          emptyLabel={t("offers.overwrite.no_results")}
        />
        {target ? (
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-600">
              {t("offers.overwrite.confirm_label", { number: expectedConfirmText })}
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500"
              placeholder={expectedConfirmText}
            />
          </div>
        ) : null}
        <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={handleClose}
            disabled={submitting}
            className="inline-flex justify-center rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-50"
          >
            {t("Cancel")}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={submitting || !canConfirm}
            className="inline-flex justify-center rounded-lg border border-transparent bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting ? t("Loading...") : t("offers.overwrite.confirm")}
          </button>
        </div>
      </div>
    </PMDialog>
  );
}

/** «Lagre som tilbudsmal» — POST /offers/:id/save_as_template. */
export function OfferSaveAsTemplateDialog({ open, onClose, offer }) {
  const { t } = useTranslation();
  const [reference, setReference] = useState("");
  const [changeNote, setChangeNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setReference("");
      setChangeNote("");
    }
  }, [open]);

  const handleClose = () => {
    if (submitting) return;
    onClose?.();
  };

  const handleConfirm = async () => {
    if (!offer?.id) return;
    setSubmitting(true);
    try {
      const payload = {};
      if (reference.trim()) payload.reference = reference.trim();
      if (changeNote.trim()) payload.change_note = changeNote.trim();
      const result = await saveOfferAsTemplate(offer.id, payload);
      if (result?.error) {
        toast.error(
          resolveOfferActionErrorMessage(t, result, "offers.save_as_template.failed")
        );
        return;
      }
      toast.success(t("offers.save_as_template.success"));
      onClose?.();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PMDialog
      open={open}
      onClose={handleClose}
      title="offers.save_as_template.title"
      size="sm"
      closeOnOutsideClick={!submitting}
    >
      <div className="space-y-4">
        <p className="text-sm text-slate-600">{t("offers.save_as_template.description")}</p>
        <div>
          <label className="mb-1 block text-xs font-semibold text-slate-600">
            {t("offers.save_as_template.reference_label")}
          </label>
          <input
            type="text"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-[#1463C7] focus:outline-none focus:ring-1 focus:ring-[#1463C7]"
            placeholder={t("offers.save_as_template.reference_placeholder")}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold text-slate-600">
            {t("offers.save_as_template.change_note_label")}
          </label>
          <textarea
            value={changeNote}
            onChange={(e) => setChangeNote(e.target.value)}
            rows={2}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-[#1463C7] focus:outline-none focus:ring-1 focus:ring-[#1463C7]"
          />
        </div>
        <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={handleClose}
            disabled={submitting}
            className="inline-flex justify-center rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-50"
          >
            {t("Cancel")}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={submitting}
            className="inline-flex justify-center rounded-lg border border-transparent bg-[#1463C7] px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-[#0B3A78] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting ? t("Loading...") : t("offers.save_as_template.confirm")}
          </button>
        </div>
      </div>
    </PMDialog>
  );
}

/** «Oppdater tilbudsmal…» — pick a template offer + POST /offers/:id/save_to_template. */
export function OfferSaveToTemplateDialog({ open, onClose, offer }) {
  const { t } = useTranslation();
  const [target, setTarget] = useState(null);
  const [changeNote, setChangeNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { query, onQueryChange, offers, isLoading } = useOfferSearch({
    open,
    excludeOfferId: offer?.id,
    templatesOnly: true,
  });

  useEffect(() => {
    if (open) {
      setTarget(null);
      setChangeNote("");
    }
  }, [open]);

  const handleClose = () => {
    if (submitting) return;
    onClose?.();
  };

  const handleConfirm = async () => {
    if (!offer?.id || !target?.id) return;
    setSubmitting(true);
    try {
      const payload = { template_id: target.id };
      if (changeNote.trim()) payload.change_note = changeNote.trim();
      const result = await saveOfferToTemplate(offer.id, payload);
      if (result?.error) {
        toast.error(
          resolveOfferActionErrorMessage(t, result, "offers.save_to_template.failed")
        );
        return;
      }
      toast.success(t("offers.save_to_template.success"));
      onClose?.();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PMDialog
      open={open}
      onClose={handleClose}
      title="offers.save_to_template.title"
      size="md"
      closeOnOutsideClick={!submitting}
    >
      <div className="space-y-4">
        <p className="text-sm text-slate-600">{t("offers.save_to_template.description")}</p>
        <SearchInput
          value={query}
          onChange={onQueryChange}
          placeholder={t("offers.save_to_template.search_placeholder")}
        />
        <OfferPickerList
          offers={offers}
          isLoading={isLoading}
          selectedId={target?.id}
          onSelect={setTarget}
          emptyLabel={t("offers.save_to_template.no_results")}
        />
        <div>
          <label className="mb-1 block text-xs font-semibold text-slate-600">
            {t("offers.save_to_template.change_note_label")}
          </label>
          <textarea
            value={changeNote}
            onChange={(e) => setChangeNote(e.target.value)}
            rows={2}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-[#1463C7] focus:outline-none focus:ring-1 focus:ring-[#1463C7]"
          />
        </div>
        <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={handleClose}
            disabled={submitting}
            className="inline-flex justify-center rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-50"
          >
            {t("Cancel")}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={submitting || !target}
            className="inline-flex justify-center rounded-lg border border-transparent bg-[#1463C7] px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-[#0B3A78] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting ? t("Loading...") : t("offers.save_to_template.confirm")}
          </button>
        </div>
      </div>
    </PMDialog>
  );
}
